import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Check, Shield, Award, Clock, Users, TrendingUp, Target, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import { testimonials } from '../mock';
import { getServiceData } from '../data/allServicesData';
import useEmblaCarousel from 'embla-carousel-react';

const ServiceDetail = ({ onOpenModal }) => {
  const { category, serviceSlug } = useParams();
  const content = getServiceData(serviceSlug, category) || {
    category: 'IP Services',
    title: 'Professional IP Services',
    subtitle: 'Expert Legal Protection',
    hero: 'Comprehensive intellectual property protection services.',
    whatIsTitle: 'What is this service?',
    whatIsContent: 'Professional legal services to protect your intellectual property.',
    keyFeatures: [],
    benefits: [],
    pricing: [],
    process: []
  };

  // Unique background images for each service (Hero Section)
  const getHeroBackground = () => {
    const imageMap = {
      // Trademark Services
      'search-registration': 'https://images.unsplash.com/photo-1635253548172-d82ffe76449d?q=80&w=2070',
      'clearance': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070',
      'application': 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?q=80&w=2070',
      'office-actions': 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1887',
      'monitoring': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015',
      'statement-of-use': 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=2070',
      'ownership-change': 'https://images.unsplash.com/photo-1552581234-26160f608093?q=80&w=2070',
      'portfolio': 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=2070',
      
      // Copyright Services
      'registration': 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070',
      'enforcement': 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?q=80&w=2012',
      'assignments': 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070',
      'litigation': 'https://images.unsplash.com/photo-1505664063633-09c83ec50e3d?q=80&w=2070',
      'dmca': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070',
      
      // Patent Services
      'searches': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=2070',
      'applications': 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=2070',
      
      // Company Formation
      'llc': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070',
      'c-corp': 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?q=80&w=2073',
      's-corp': 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=2070',
      'non-profit': 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?q=80&w=2073'
    };
    
    return imageMap[serviceSlug] || 'https://images.unsplash.com/photo-1635253548172-d82ffe76449d?q=80&w=2070';
  };

  // Specific images for "What Is Section" - Contextually relevant to each service
  const getWhatIsImage = () => {
    const imageMap = {
      // Trademark Services
      'search-registration': 'https://images.pexels.com/photos/7948058/pexels-photo-7948058.jpeg?auto=compress&cs=tinysrgb&w=2070',
      'clearance': 'https://images.pexels.com/photos/1194775/pexels-photo-1194775.jpeg?auto=compress&cs=tinysrgb&w=1260',
      'application': 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070',
      'office-actions': 'https://images.unsplash.com/photo-1619418602850-35ad20aa1700?q=80&w=2070',
      'monitoring': 'https://images.unsplash.com/photo-1586769852836-bc069f19e1b6?q=80&w=2070',
      'statement-of-use': 'https://images.pexels.com/photos/48148/document-agreement-documents-sign-48148.jpeg?auto=compress&cs=tinysrgb&w=1260',
      'ownership-change': 'https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=2070',
      'portfolio': 'https://images.unsplash.com/photo-1468779036391-52341f60b55d?q=80&w=2070',
      
      // Copyright Services
      'registration': 'https://images.unsplash.com/photo-1593444285553-28163240e3f1?q=80&w=2070',
      'copyright-enforcement': 'https://images.unsplash.com/photo-1676181739859-08330dea8999?q=80&w=2070',
      'enforcement': 'https://images.unsplash.com/photo-1676181739859-08330dea8999?q=80&w=2070',
      'assignments': 'https://images.unsplash.com/photo-1603796846097-bee99e4a601f?q=80&w=2070',
      'litigation': 'https://images.unsplash.com/photo-1589994965851-a8f479c573a9?q=80&w=2070',
      'copyright-portfolio': 'https://images.pexels.com/photos/3771110/pexels-photo-3771110.jpeg?auto=compress&cs=tinysrgb&w=1260',
      'dmca': 'https://images.unsplash.com/photo-1516382799247-87df95d790b7?q=80&w=2070',
      
      // Patent Services
      'searches': 'https://images.pexels.com/photos/1192333/pexels-photo-1192333.jpeg?auto=compress&cs=tinysrgb&w=1260',
      'patent-office-actions': 'https://images.unsplash.com/photo-1676181739500-e1291b113370?q=80&w=2070',
      'applications': 'https://images.unsplash.com/photo-1521791055366-0d553872125f?q=80&w=2070',
      'enforcement': 'https://images.unsplash.com/photo-1687289133469-b2a07a13b78b?q=80&w=2070',
      
      // Company Formation
      'llc': 'https://images.unsplash.com/photo-1638262052640-82e94d64664a?q=80&w=2070',
      'c-corp': 'https://images.unsplash.com/photo-1613725252817-c231df2c72e8?q=80&w=2070',
      's-corp': 'https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?q=80&w=2070',
      'non-profit': 'https://images.pexels.com/photos/618158/pexels-photo-618158.jpeg?auto=compress&cs=tinysrgb&w=1260'
    };
    
    return imageMap[serviceSlug] || 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070';
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation
  const validatePhone = (phone) => {
    const phoneRegex = /^\+\d{1,3}[\s\-\.]?\(?\d{3}\)?[\s\-\.]?\d{3}[\s\-\.]?\d{4}$/;
    return phoneRegex.test(phone);
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhone(formData.phone)) {
      setErrors(prev => ({ ...prev, phone: 'Please enter a valid phone number with country code (e.g., +1 123-456-7890)' }));
    } else {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate before submission
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
      hasErrors = true;
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number with country code (e.g., +1 123-456-7890)';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiService.createServiceInquiry({
        serviceType: serviceSlug || 'general',
        ...formData
      });
      toast.success(response.message);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setErrors({ email: '', phone: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section with Background Image */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${getHeroBackground()})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#012169]/90 via-[#013d9e]/90 to-[#012169]/95"></div>
        </div>

        {/* Animated Background Shapes */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#F97316] rounded-full blur-3xl opacity-20"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#F97316] rounded-full blur-3xl opacity-20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block mb-4">
              <p className="text-[#F97316] text-sm font-bold uppercase tracking-wider px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                {content.category}
              </p>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight" style={{ textShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
              {content.title}
            </h1>
            <p className="text-base md:text-lg text-gray-100 mb-10 max-w-4xl mx-auto leading-relaxed" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
              {content.hero}
            </p>
          </div>
        </div>
      </section>

      {/* What Is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Two Column Layout: Image Left, Content Right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left Column - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={getWhatIsImage()}
                  alt={content.title}
                  className="w-full h-[500px] object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#012169]/30 to-transparent"></div>
              </div>
              {/* Decorative Element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#F97316] rounded-full blur-3xl opacity-20"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#F97316] rounded-full blur-3xl opacity-20"></div>
            </div>

            {/* Right Column - Content */}
            <div>
              <h2 className="text-4xl font-bold text-[#012169] mb-6">{content.whatIsTitle}</h2>
              <h3 className="text-2xl font-semibold text-[#F97316] mb-6">Your Brand's Legal Shield</h3>
              <p className="text-base text-gray-700 leading-relaxed mb-8">
                {content.whatIsContent}
              </p>
              {content.quote && (
                <div className="bg-gradient-to-r from-[#012169] to-[#8B5CF6] p-6 rounded-xl border-l-4 border-[#F97316]">
                  <p className="text-base text-gray-200 italic">"{content.quote}"</p>
                </div>
              )}
            </div>
          </div>

          {/* What This Service Protects */}
          {content.protects && content.protects.length > 0 && (
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-[#012169] text-center mb-10">What a Trademark Protects</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {content.protects.map((item, index) => {
                  // Service-specific unique images for protection cards
                  const getProtectionImages = () => {
                    // Trademark services
                    if (serviceSlug === 'search-registration' || serviceSlug === 'clearance' || serviceSlug === 'application') {
                      return [
                        'https://images.unsplash.com/photo-1599658880436-c61792e70672?q=80&w=2070', // Brand identity
                        'https://images.unsplash.com/photo-1556155092-490a1ba16284?q=80&w=2070', // Logos/designs
                        'https://images.unsplash.com/photo-1553729459-efe14ef6055d?q=80&w=2070'  // Marketing
                      ];
                    }
                    // Copyright services
                    if (serviceSlug === 'registration' || serviceSlug === 'assignments' || serviceSlug === 'litigation') {
                      return [
                        'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=2070', // Books/writing
                        'https://images.unsplash.com/photo-1�511671782779-c97d3d27a1d4?q=80&w=2070', // Music/art
                        'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?q=80&w=2070'  // Software/tech
                      ];
                    }
                    // Patent services
                    if (serviceSlug === 'searches' || serviceSlug === 'applications' || serviceSlug === 'enforcement') {
                      return [
                        'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=2070', // Innovation
                        'https://images.unsplash.com/photo-1581093588401-fbb62a02f120?q=80&w=2070', // Technical
                        'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2070'  // Research
                      ];
                    }
                    // Company formation
                    if (serviceSlug === 'llc' || serviceSlug === 'c-corp' || serviceSlug === 's-corp' || serviceSlug === 'non-profit') {
                      return [
                        'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070', // Business planning
                        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2070', // Analytics/growth
                        'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=2070'  // Team/collaboration
                      ];
                    }
                    // Default fallback
                    return [
                      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070',
                      'https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=2070',
                      'https://images.unsplash.com/photo-1635253548172-d82ffe76449d?q=80&w=2070'
                    ];
                  };
                  
                  const protectionImages = getProtectionImages();
                  
                  return (
                    <div key={index} className="group bg-white rounded-xl overflow-hidden shadow-md border-2 border-gray-200 hover:border-[#012169] hover:shadow-xl transition-all duration-300">
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={protectionImages[index % protectionImages.length]}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#012169]/80 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <h4 className="text-xl font-bold text-white">{item.title}</h4>
                        </div>
                      </div>
                      {/* Content */}
                      <div className="p-6">
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section - Modern Timeline Design */}
      {content.process && content.process.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-white via-[#012169]/5 to-white relative overflow-hidden">
          {/* Decorative Background Elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#012169]/10 rounded-full blur-3xl"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-block mb-4">
                <span className="px-6 py-2 bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white text-sm font-bold uppercase tracking-wider rounded-full shadow-lg">
                  How It Works
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-[#012169] mb-4">
                Your {content.title} Journey in {content.process.length} Easy Steps
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our streamlined process makes {content.title.toLowerCase()} easy and efficient
              </p>
            </div>

            {/* Horizontal Timeline - Desktop */}
            <div className="hidden md:block">
              <div className="relative">
                {/* Connecting Line */}
                <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-[#012169] via-[#F97316] to-[#8B5CF6]"></div>
                
                <div className={`grid grid-cols-${content.process.length} gap-8`}>
                  {content.process.map((step, index) => (
                    <div key={index} className="relative group">
                      {/* Circle Node */}
                      <div className="flex justify-center mb-6">
                        <div className="relative">
                          <div className="w-20 h-20 bg-gradient-to-br from-[#012169] to-[#8B5CF6] rounded-full flex items-center justify-center shadow-2xl border-4 border-white group-hover:scale-110 transition-transform duration-300">
                            <span className="text-3xl font-black text-white">{index + 1}</span>
                          </div>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-200 group-hover:border-[#012169] group-hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
                        {/* Title */}
                        <h3 className="text-xl font-bold text-[#012169] mb-3 text-center group-hover:text-[#F97316] transition-colors duration-300">
                          {step.title}
                        </h3>
                        
                        {/* Description */}
                        <p className="text-sm text-gray-600 leading-relaxed text-center">
                          {step.desc}
                        </p>

                        {/* Arrow for flow */}
                        {index < content.process.length - 1 && (
                          <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 hidden lg:block">
                            <svg className="w-8 h-8 text-[#F97316]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Vertical Layout */}
            <div className="md:hidden space-y-6">
              {content.process.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex gap-4">
                    {/* Number Badge */}
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#012169] to-[#8B5CF6] rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                        <span className="text-2xl font-black text-white">{index + 1}</span>
                      </div>
                      {index < content.process.length - 1 && (
                        <div className="w-0.5 h-20 bg-gradient-to-b from-[#012169] to-[#F97316] mx-auto mt-2"></div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-200 hover:border-[#012169] hover:shadow-2xl transition-all duration-300">
                      <h3 className="text-lg font-bold text-[#012169] mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      <section className="py-20 bg-white" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#012169] mb-4">{content.title} Pricing</h2>
            <p className="text-base text-gray-600">Transparent pricing with no hidden fees. Choose the package that fits your needs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(content.pricing && content.pricing.length > 0 ? content.pricing : [
              { name: 'Basic', price: 299, features: ['Service Feature 1', 'Service Feature 2', 'Service Feature 3', 'Email Support'] },
              { name: 'Professional', price: 599, popular: true, features: ['Everything in Basic', 'Feature 4', 'Feature 5', 'Phone Support'] },
              { name: 'Enterprise', price: 'Custom', features: ['Everything in Professional', 'Premium Feature', 'Dedicated Manager', '24/7 Support'] }
            ]).map((plan, index) => {
              // Check if this is a trademark service
              const isTrademarkService = content.category === 'Trademark Services';
              const showPrice = isTrademarkService;
              
              return (
              <Card key={index} className={`p-6 flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${plan.popular ? 'border-4 border-[#012169] shadow-2xl' : 'border-2 border-gray-200 hover:border-[#012169]'}`}>
                {plan.popular && (
                  <div className="bg-[#F97316] text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
                )}
                <h3 className="text-2xl font-bold text-[#012169] mb-2">{plan.name}</h3>
                <div className="mb-2">
                  {showPrice ? (
                    <>
                      <span className="text-5xl font-bold text-[#F97316]">{typeof plan.price === 'number' ? `$${plan.price}` : plan.price}</span>
                      {plan.description && <span className="text-gray-600 text-sm"> {plan.description}</span>}
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-[#012169]">Get a Quote</span>
                  )}
                </div>
                {plan.subtext && (
                  <p className="text-gray-600 text-sm mb-6">{plan.subtext}</p>
                )}
                
                {/* Scrollable Features List */}
                <div className="flex-1 overflow-y-auto max-h-64 mb-6 pr-2 scrollbar-thin scrollbar-thumb-[#012169] scrollbar-track-gray-100">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <Check className="h-5 w-5 text-[#F97316] flex-shrink-0 mt-0.5" />
                        <span className="text-base text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Button at bottom */}
                <Button onClick={() => onOpenModal && onOpenModal(`Get Started - ${plan.name} - ${content.title}`)} className="w-full bg-[#F97316] text-white font-semibold mt-auto hover:bg-[#e5a200] transition-colors">
                  {showPrice ? 'Get Started' : 'Request Quote'}
                </Button>
              </Card>
            )})}
          </div>
        </div>
      </section>
      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#012169] text-center mb-12">Why Choose Our {content.title}?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.benefits && content.benefits.length > 0 ? content.benefits.map((benefit, index) => {
              const icons = [Shield, Award, Clock, Users, TrendingUp, Target, Zap, Check];
              const IconComponent = icons[index % icons.length];
              return (
                <Card key={index} className="p-6 border-2 border-gray-200 hover:border-[#012169] hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <IconComponent className="h-10 w-10 text-[#F97316] mb-4" />
                  <h3 className="text-xl font-bold text-[#012169] mb-2">{benefit.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{benefit.desc}</p>
                </Card>
              );
            }) : (
              [Shield, Award, Clock, Users].map((Icon, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow duration-300">
                  <Icon className="h-12 w-12 text-[#F97316] mb-4" />
                  <h3 className="text-xl font-bold text-[#012169] mb-2">Benefit {index + 1}</h3>
                  <p className="text-gray-600">Professional service benefit description</p>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialsSlider />

      {/* CTA Section with Background Image */}
      <section 
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#012169]/95 via-[#013d9e]/90 to-[#012169]/95"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Protect Your Rights Today
          </h2>
          <p className="text-base md:text-lg text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed">
            Don't wait to secure your intellectual property. Get expert legal assistance from experienced attorneys and safeguard your business future.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <Button 
              size="lg"
              onClick={() => onOpenModal && onOpenModal(`Get Started Now - ${content.title}`)}
              className="bg-[#F97316] text-white font-bold text-base px-10 py-7 rounded-lg shadow-2xl hover:bg-[#e5a200] hover:shadow-[0_20px_40px_rgba(249,115,22,0.3)] transition-all duration-300"
            >
              Get Started Now
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => onOpenModal && onOpenModal(`Schedule Consultation - ${content.title}`)}
              className="border-3 border-white text-white font-bold text-base px-10 py-7 rounded-lg hover:bg-white hover:text-[#012169] transition-all duration-300"
            >
              Schedule Consultation
            </Button>
          </div>
          
          {/* Contact Info */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-white">
          </div>
        </div>
      </section>
    </div>
  );
};

// Testimonials Slider Component
const TestimonialsSlider = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    slidesToScroll: 1
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-[#012169] text-center mb-12">What Our Clients Say</h2>
        
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-lg rounded-full p-3"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6 text-[#012169]" />
          </button>
          
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white shadow-lg rounded-full p-3"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6 text-[#012169]" />
          </button>

          {/* Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="flex-[0_0_100%] md:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)] min-w-0">
                  <Card className="p-6 h-full">
                    <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.content}"</p>
                    <div className="flex items-center space-x-4 mt-auto">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name} 
                        className="w-14 h-14 rounded-full object-cover border-2 border-[#F97316]" 
                      />
                      <div>
                        <h4 className="font-semibold text-[#012169]">{testimonial.name}</h4>
                        <p className="text-sm text-gray-600">{testimonial.role}</p>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceDetail;
